import { Bell, AlertTriangle, X, CalendarDays, UserMinus, UserPlus, UserCheck, Check, ClipboardCheck, Clock, Pin, Mail, MailOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { API } from '@/lib/helpers';

function fmtDateTime(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', ' às');
  } catch { return ''; }
}

function fmtOverdue(hours) {
  if (hours < 24) return `${Math.round(hours)}h atraso`;
  return `${Math.round(hours / 24)}d atraso`;
}

const NOTIF_CONFIG = {
  ausencia_substituto:    { icon: UserMinus,    color: 'text-red-500',    bg: 'bg-red-50',    badgeCls: 'bg-red-50 text-red-600',       modulo: 'Escalas',   acao: 'Ausência',       persistent: false },
  ausencia_sem_substituto:{ icon: UserMinus,    color: 'text-orange-500', bg: 'bg-orange-50', badgeCls: 'bg-orange-50 text-orange-600', modulo: 'Escalas',   acao: 'Sem Substituto', persistent: true },
  novo_designado:         { icon: UserPlus,     color: 'text-blue-500',   bg: 'bg-blue-50',   badgeCls: 'bg-blue-50 text-blue-600',     modulo: 'Escalas',   acao: 'Designação',     persistent: true },
  voltou_presente:        { icon: UserCheck,    color: 'text-green-500',  bg: 'bg-green-50',  badgeCls: 'bg-green-50 text-green-600',   modulo: 'Presença',  acao: 'Retorno',        persistent: false },
  event:                  { icon: CalendarDays, color: 'text-amber-600',  bg: 'bg-amber-50',  badgeCls: 'bg-amber-50 text-amber-600',   modulo: 'Eventos',   acao: 'Atrasado',       persistent: true },
  task:                   { icon: ClipboardCheck, color: 'text-amber-600', bg: 'bg-amber-50', badgeCls: 'bg-amber-50 text-amber-600',   modulo: 'Tarefas',   acao: 'Pendente',       persistent: true },
};

export function NotificationBanner({ notifications, dismissed, onDismiss }) {
  const unread = notifications.filter(n => !n.read);
  if (unread.length === 0 || dismissed) return null;

  return (
    <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-2rem)] max-w-lg animate-in slide-in-from-top-2 fade-in duration-300" data-testid="notification-banner">
      <div className="bg-amber-50 border border-amber-300 rounded-xl shadow-lg">
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            </div>
            <p className="text-sm text-amber-800 font-medium truncate">
              {unread.length === 1
                ? (unread[0].short_message || unread[0].name || 'Nova notificação')
                : `${unread.length} notificações pendentes`}
            </p>
          </div>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-400 hover:text-amber-600 hover:bg-amber-100 rounded-full flex-shrink-0"
            onClick={(e) => { e.stopPropagation(); onDismiss(); }} data-testid="notification-banner-dismiss">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function NotificationBellIcon({ notifications, onClick }) {
  const unreadCount = notifications.filter(n => !n.read).length;
  return (
    <Button variant="ghost" size="sm" className="relative p-2" onClick={onClick} data-testid="notification-bell">
      <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-amber-600' : 'text-slate-500'}`} />
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex h-4.5 w-4.5 items-center justify-center" data-testid="notification-badge">
          <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        </span>
      )}
    </Button>
  );
}

export function NotificationModal({ open, onOpenChange, notifications, onRefresh }) {
  const unreadCount = notifications.filter(n => !n.read).length;

  const handleToggleRead = async (notifId, currentlyRead) => {
    try {
      const endpoint = currentlyRead ? 'unread' : 'read';
      await fetch(`${API}/notifications/${notifId}/${endpoint}`, { method: 'PUT', credentials: 'include' });
      if (onRefresh) setTimeout(onRefresh, 100);
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, { method: 'PUT', credentials: 'include' });
      if (onRefresh) setTimeout(onRefresh, 100);
    } catch {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="max-w-3xl w-full max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Notificações</DialogTitle>
                <DialogDescription className="text-sm text-slate-500">
                  {unreadCount > 0 ? `${unreadCount} não lida${unreadCount > 1 ? 's' : ''} de ${notifications.length}` : `${notifications.length} notificações`}
                </DialogDescription>
              </div>
            </div>
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={handleMarkAllRead} className="text-xs h-8 bg-white" data-testid="mark-all-read-btn">
                <Check className="w-3.5 h-3.5 mr-1" />Marcar tudo como lido
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="notification-list">
          {notifications.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Bell className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Nenhuma notificação</p>
            </div>
          ) : (
            notifications.map((n, i) => (
              <NotificationCard key={n.notification_id || n.event_id || n.tarefa_id || i} n={n} onToggleRead={handleToggleRead} />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NotificationCard({ n, onToggleRead }) {
  const hasNotifId = !!n.notification_id;
  const config = NOTIF_CONFIG[n.type] || NOTIF_CONFIG.event;
  const Icon = config.icon;
  const isRead = n.read === true;

  let description, dateStr;
  if (hasNotifId) {
    description = n.message;
    dateStr = fmtDateTime(n.created_at);
  } else {
    const isTask = n.type === 'task';
    description = isTask ? n.descricao : n.name;
    dateStr = n.hours_overdue != null ? fmtOverdue(n.hours_overdue) : '';
  }

  return (
    <div className="border border-slate-100 rounded-lg mb-2 bg-white relative overflow-hidden" data-testid={`notif-${n.notification_id || n.event_id || n.tarefa_id}`}>
      {/* Ribbon sutil de status */}
      <div className={`absolute top-0 left-0 w-1 h-full`} style={{ backgroundColor: isRead ? '#eaeaea' : '#333333' }} />
      <div className="flex items-center gap-3 px-4 py-3 pl-5">
        <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-3.5 h-3.5 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Badge className={`text-[10px] px-1.5 py-0 ${config.badgeCls}`}>{config.acao}</Badge>
            {config.persistent && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center text-amber-500"><Pin className="w-3 h-3" /></span>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">Persistente até ser resolvida</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <p className="text-xs text-slate-700 leading-snug pr-2">{description}</p>
          <div className="flex items-center gap-1.5 mt-1">
            {dateStr && (
              <>
                <Clock className="w-3 h-3 text-slate-400" />
                <span className="text-xs text-slate-400">{dateStr}</span>
                <span className="text-xs text-slate-300">|</span>
              </>
            )}
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-200">{config.modulo}</Badge>
          </div>
        </div>
        {hasNotifId && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon"
                  onClick={() => onToggleRead(n.notification_id, isRead)}
                  className="flex-shrink-0 h-8 w-8 text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  data-testid={`toggle-read-${n.notification_id}`}>
                  {isRead ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">{isRead ? 'Marcar como não lida' : 'Marcar como lida'}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}

export function NotificationDropdown() { return null; }
